
import { useState, useEffect } from "react";
import ApproachTest from "@/components/ApproachTest";
import ApproachesHeader from "@/components/approaches/ApproachesHeader";
import ApproachesTitle from "@/components/approaches/ApproachesTitle";
import ApproachCardGrid from "@/components/approaches/ApproachCardGrid";
import ApproachesFavorites from "@/components/approaches/ApproachesFavorites";
import NavigationMenu from "@/components/layout/NavigationMenu";
import { PuttSituation } from "@/types/golf";
import { liftedApproaches } from "@/data/approaches/liftedApproaches";
import { longChips } from "@/data/approaches/longChips";
import { shortChips } from "@/data/approaches/shortChips"; 
import { wedging50m } from "@/data/approaches/wedging50m";
import { wedging30m } from "@/data/approaches/wedging30m";
import { shortBunker } from "@/data/approaches/shortBunker";
import { longBunker } from "@/data/approaches/longBunker";
import { midDistanceApproaches } from "@/data/approaches/midDistanceApproaches";
import { roughApproaches } from "@/data/approaches/roughApproaches";
import { useFavorites } from "@/hooks/useFavorites";

const Approaches = () => {
  const [selectedTest, setSelectedTest] = useState<PuttSituation | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const { refreshFavorites } = useFavorites();

  // Import all the approach situations
  const situations = [
    liftedApproaches,
    longChips,
    shortChips,
    wedging50m,
    wedging30m,
    shortBunker,
    longBunker,
    midDistanceApproaches,
    roughApproaches
  ];

  useEffect(() => {
    // Refresh favorites whenever the component mounts or refreshKey changes
    refreshFavorites();
  }, [refreshKey, refreshFavorites]);

  const handleSelectTest = (test: PuttSituation) => {
    setSelectedTest(test);
    
    // Force refresh of favorites data with a slight delay to ensure DB operation completes
    setTimeout(() => {
      refreshFavorites();
      setRefreshKey(prevKey => prevKey + 1);
    }, 300);
  };

  // Add a function to refresh favorites that can be used after toast notifications
  const refreshAfterToast = () => {
    setTimeout(() => {
      refreshFavorites();
      setRefreshKey(prevKey => prevKey + 1);
    }, 300);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-golf-50 to-white">
      <NavigationMenu />
      <div className="container mx-auto py-8">
        <ApproachesHeader />
        
        {selectedTest ? (
          <ApproachTest 
            situation={selectedTest}
            onClose={() => {
              setSelectedTest(null);
              // Refresh favorites when closing a test
              setTimeout(() => {
                refreshFavorites();
                setRefreshKey(prevKey => prevKey + 1);
              }, 300);
            }}
            refreshAfterToast={refreshAfterToast}
          />
        ) : (
          <>
            <ApproachesTitle />
            
            <ApproachesFavorites 
              onSelectTest={handleSelectTest} 
              situations={situations}
              refreshAfterToast={refreshAfterToast}
              key={`favorites-${refreshKey}`} // Use key with refreshKey to force re-render
            />
            
            <ApproachCardGrid 
              onSelectTest={handleSelectTest}
              refreshAfterToast={refreshAfterToast}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default Approaches;
